// Det der er på skærmen: hver grafik følger controllerens tilstand og
// animerer ind, mellem sider og ud. Bruges af vMix-outputtet og af
// controllerens program-skærm.

import { useEffect, useMemo, useRef } from "react";
import type { GfxGraphicId, GfxGraphicState, GfxSnapshot } from "@mma/shared";
import { GRAPHIC_BY_ID, resolve, type GfxCtx, type GraphicDef } from "./graphics.js";
import { StagePlayer } from "./player.js";
import { Stage } from "./Stage.js";
import { SpotterPresence } from "./spotter/SpotterGraphics.js";
import type { GtStoryboardKey } from "./spec.js";

const OFF: GfxGraphicState = { onAir: false, steps: [], cue: 0, fields: {} };

const sameStart = (a: string[], b: string[]) => a.every((x, i) => b[i] === x);

// Spotter-grafik: monteres når den går på (CSS-intro), fader ud når den tages af.
function OnAirHtml({ def, g, ctx, auto }: { def: GraphicDef; g: GfxGraphicState; ctx: GfxCtx; auto: boolean }) {
  const firstCue = useRef<number | null>(null);
  if (firstCue.current === null) firstCue.current = g.onAir ? g.cue : -1;
  const inCue = useRef(g.cue);
  if (g.onAir) inCue.current = g.cue;
  const { texts } = resolve(def, g.fields, ctx);
  const Html = def.Html!;
  return (
    <SpotterPresence
      id={def.id}
      onAir={auto || g.onAir}
      cue={auto ? 0 : inCue.current}
      instant={!auto && inCue.current === firstCue.current}
    >
      <Html ctx={ctx} texts={texts} />
    </SpotterPresence>
  );
}

function OnAirGraphic({ def, g, ctx, auto }: { def: GraphicDef; g: GfxGraphicState; ctx: GfxCtx; auto: boolean }) {
  const player = useMemo(() => new StagePlayer(def.spec!), [def.spec]);
  const seen = useRef<GfxGraphicState | null>(null);

  useEffect(() => {
    const before = seen.current;
    seen.current = g;

    // auto: grafikken er altid "på" og spiller ind når siden indlæses, så
    // inputtet kan styres med vMix' egne overlay-knapper.
    if (auto) {
      if (!before) player.play("in");
      else if (g.onAir && g.steps.length === before.steps.length + 1 && g.steps.length > 1) {
        player.play(g.steps.at(-1) as GtStoryboardKey);
      }
      return;
    }
    if (!before) {
      // Første besked efter indlæsning: spring til slutstillingen uden animation.
      if (g.onAir) player.show(g.steps);
      return;
    }
    if (g.cue === before.cue) return;
    if (!g.onAir) {
      if (before.onAir) player.playOut();
      return;
    }
    const appended = before.onAir && g.steps.length === before.steps.length + 1 && sameStart(before.steps, g.steps);
    if (g.steps.length === 1) player.play("in");
    else if (appended) player.play(g.steps.at(-1) as GtStoryboardKey);
    else player.show(g.steps);
  }, [g, auto, player]);

  const { texts, visible, images } = resolve(def, g.fields, ctx);
  return (
    <Stage spec={def.spec!} player={player} texts={texts} visible={visible} images={images}>
      {def.Extra && <def.Extra ctx={ctx} />}
    </Stage>
  );
}

export function GfxScreen({
  snapshot,
  now,
  only,
  auto = false,
}: {
  snapshot: GfxSnapshot;
  now: number;
  only?: GfxGraphicId[];
  auto?: boolean;
}) {
  const ctx: GfxCtx = { data: snapshot.data, state: snapshot.state, now };
  const ids = only ?? (Object.keys(GRAPHIC_BY_ID) as GfxGraphicId[]);
  return (
    <>
      {ids.map((id) => {
        const def = GRAPHIC_BY_ID[id];
        const g = snapshot.state.graphics[id] ?? OFF;
        return def.Html ? (
          <OnAirHtml key={id} def={def} g={g} ctx={ctx} auto={auto} />
        ) : (
          <OnAirGraphic key={id} def={def} g={g} ctx={ctx} auto={auto} />
        );
      })}
    </>
  );
}
